/**
 * 微信 JS-SDK 图片多选工具（维保师傅在微信内置浏览器里用）
 *
 * 背景：微信安卓 webview 对 <input type="file" multiple> 支持不稳定（常常只能单选），
 * 所以照片多选走微信官方相册 wx.chooseImage（一次最多 9 张），
 * 再用 wx.getLocalImgData 取回 base64，转成 File 交给原有上传链路（后端零改动）。
 *
 * 注意：调用前页面必须先 wx.config 注入 'chooseImage'、'getLocalImgData' 权限。
 */
import wx from 'weixin-js-sdk'

/** 是否在微信内置浏览器中 */
export const isWechatEnv = () => {
  try {
    return /micromessenger/i.test(navigator.userAgent || '')
  } catch (e) {
    return false
  }
}

/** 单张 localId -> base64 dataUrl */
const getLocalImgData = (localId) => new Promise((resolve, reject) => {
  wx.getLocalImgData({
    localId,
    success: (res) => resolve(res && res.localData),
    fail: (err) => reject(err)
  })
})

/**
 * 统一成带前缀的 dataUrl
 * - iOS(jssdk>=1.4)：返回 "data:image/jpg;base64,xxxx"
 * - 部分安卓：返回纯 base64 字符串，需要自己补前缀
 * - 极老版本：可能返回 wxLocalResource:// 之类的 localId（无法转 base64，调用方会跳过）
 */
const normalizeBase64 = (data) => {
  const d = (data || '').trim()
  if (!d) return ''
  if (d.indexOf('data:image') === 0) return d
  if (d.indexOf('wxLocalResource') === 0 || d.indexOf('weixin://') === 0) return ''
  return 'data:image/jpeg;base64,' + d
}

/** base64 dataUrl -> File 对象（与 ImagePicker 的 file 结构保持一致，走原上传链路） */
export const dataUrlToFile = (dataUrl, filename) => {
  try {
    const arr = dataUrl.split(',')
    const mimeMatch = arr[0].match(/:(.*?);/)
    const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg'
    const bstr = atob(arr[1])
    let n = bstr.length
    const u8arr = new Uint8Array(n)
    while (n--) u8arr[n] = bstr.charCodeAt(n)
    return new File([u8arr], filename, { type: mime })
  } catch (e) {
    return null
  }
}

/**
 * 调用微信相册多选图片
 * @param {number} count 最多可选张数（1~9）
 * @returns {Promise<string[]>} base64 dataUrl 列表
 */
export const chooseImagesByWx = (count) => new Promise((resolve, reject) => {
  const max = Math.max(1, Math.min(9, Number(count) || 1))
  let settled = false
  // 超时兜底：微信接口偶发既不 success 也不 fail，避免页面一直转圈
  const timer = setTimeout(() => {
    if (!settled) {
      settled = true
      reject(new Error('微信选图超时'))
    }
  }, 30000)
  const done = (fn, arg) => {
    if (settled) return
    settled = true
    clearTimeout(timer)
    fn(arg)
  }
  try {
    wx.chooseImage({
      count: max,
      sizeType: ['compressed'], // 微信侧先压缩，避免原图过大
      sourceType: ['album', 'camera'],
      success: async (res) => {
        const localIds = (res && res.localIds) || []
        const list = []
        for (let i = 0; i < localIds.length; i++) {
          try {
            const d = normalizeBase64(await getLocalImgData(localIds[i]))
            if (d) list.push(d)
          } catch (e) {
            // 单张失败不影响其它张
          }
        }
        if (!list.length) {
          done(reject, new Error('图片读取失败'))
          return
        }
        done(resolve, list)
      },
      fail: (err) => done(reject, err)
    })
  } catch (e) {
    done(reject, e)
  }
})
